import { useState, useRef, useCallback, useEffect } from 'react';
import { Microphone, CircleNotch } from '@phosphor-icons/react';
import { apiUrl, buildAuthHeaders } from '../../config/api';

const MAX_RECORDING_MS = 60 * 1000;

const hasMediaRecorder =
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!navigator?.mediaDevices?.getUserMedia;

function pickMimeType(): string | undefined {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/aac',
    'audio/webm',
  ];
  for (const type of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type;
    } catch {
      // ignore
    }
  }
  return undefined;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
}

type Phase = 'idle' | 'recording' | 'transcribing';

export default function VoiceInputButton({ onTranscript, onError, disabled }: VoiceInputButtonProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const cleanupStream = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      try {
        recorderRef.current?.stop();
      } catch {
        // ignore
      }
      cleanupStream();
    };
  }, [cleanupStream]);

  const handleStop = useCallback(
    async (mimeType: string) => {
      const chunks = chunksRef.current;
      cleanupStream();
      if (cancelledRef.current) return;
      if (!chunks.length) {
        setPhase('idle');
        return;
      }
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < 1024) {
        setPhase('idle');
        onError?.("I didn't catch that — try recording again.");
        return;
      }
      setPhase('transcribing');
      try {
        const base64 = await blobToBase64(blob);
        const headers = await buildAuthHeaders();
        const res = await fetch(apiUrl('/transcribe'), {
          method: 'POST',
          headers,
          body: JSON.stringify({ audio: base64, mimeType }),
        });
        const json = (await res.json().catch(() => null)) as
          | { success: boolean; data?: { transcript?: string }; error?: string }
          | null;
        if (!res.ok || !json?.success) {
          onError?.(json?.error || 'Could not transcribe audio. Please try again.');
          return;
        }
        const transcript = json.data?.transcript?.trim();
        if (transcript) {
          onTranscript(transcript);
        } else {
          onError?.("I didn't catch any speech — try again.");
        }
      } catch (err) {
        console.error('Transcribe upload failed:', err);
        onError?.('Could not reach transcription service. Please try again.');
      } finally {
        if (!cancelledRef.current) setPhase('idle');
      }
    },
    [cleanupStream, onError, onTranscript],
  );

  const startRecording = useCallback(async () => {
    if (!hasMediaRecorder) {
      onError?.('Voice input is not supported on this device.');
      return;
    }
    setPhase('recording');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setPhase('idle');
      const name = (err as DOMException)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        onError?.('Microphone access is required for voice input. Enable it in Settings.');
      } else if (name === 'NotFoundError') {
        onError?.('No microphone was found on this device.');
      } else {
        onError?.("Couldn't access the microphone. Please try again.");
      }
      return;
    }

    streamRef.current = stream;
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      console.error('MediaRecorder init failed:', err);
      cleanupStream();
      setPhase('idle');
      onError?.('Voice input is not supported on this device.');
      return;
    }

    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      cleanupStream();
      setPhase('idle');
      onError?.('Recording failed. Please try again.');
    };
    recorder.onstop = () => {
      const finalType = recorder.mimeType || mimeType || 'audio/webm';
      void handleStop(finalType);
    };

    recorder.start();
    stopTimerRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          // ignore
        }
      }
    }, MAX_RECORDING_MS);
  }, [cleanupStream, handleStop, onError]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    try {
      recorder.stop();
    } catch {
      // ignore — onstop may already have fired
    }
  }, []);

  const toggle = useCallback(() => {
    if (phase === 'transcribing') return;
    if (phase === 'recording') {
      stopRecording();
      return;
    }
    void startRecording();
  }, [phase, startRecording, stopRecording]);

  if (!hasMediaRecorder) return null;

  const isBusy = phase === 'transcribing';
  const isRecording = phase === 'recording';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || isBusy}
      aria-pressed={isRecording}
      aria-label={
        isRecording ? 'Stop recording' : isBusy ? 'Transcribing voice input' : 'Start voice input'
      }
      title={isRecording ? 'Stop recording' : isBusy ? 'Transcribing…' : 'Voice input'}
      className={`p-1.5 rounded-full transition-all ${
        isRecording
          ? 'text-pink-500 bg-pink-100 dark:bg-pink-900/30 animate-pulse'
          : isBusy
            ? 'text-pink-500'
            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {isBusy ? (
        <CircleNotch size={18} className="animate-spin" />
      ) : (
        <Microphone size={18} weight={isRecording ? 'fill' : 'regular'} />
      )}
    </button>
  );
}
